import type { LucideIcon } from "lucide-react";
import { ForwardRefExoticComponent, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckedLink } from "./routeGenerator";
import { useNavigationTransition } from "./useNavigationTransition";
import {
    SidebarMenuButton,
    SidebarMenuSubButton,
    useSidebar
} from "@/components/ui/sidebar";
import { Loader } from "@/frontend/@ui/Loader";
import { cn } from "@/lib/utils";

interface IProps {
    to: CheckedLink;
    children: ReactNode;
    className?: string;
    icon?: LucideIcon;
    isActive?: boolean;
}

export const TransitionSidebarMenuButton = (props: IProps) => {
    return <Button {...props} Component={SidebarMenuButton} />;
};

export const TransitionSidebarMenuSubButton = (props: IProps) => {
    return <Button {...props} Component={SidebarMenuSubButton} />;
};

const Button = ({
    to,
    children,
    icon: Icon,
    isActive,
    className,
    Component
}: IProps & {
    Component: ForwardRefExoticComponent<{
        asChild: boolean | undefined;
        isActive: boolean | undefined;
        className: string | undefined;
        children: ReactNode;
    }>;
}) => {
    const location = useLocation();

    const { setOpenMobile } = useSidebar();
    const onTransitionEnd = () => {
        setOpenMobile(false);
    };

    const { isPending, spread } = useNavigationTransition({
        to,
        onTransitionEnd
    });

    return (
        <Component
            asChild
            isActive={isActive ?? location.pathname === to.path}
            className={cn(isPending && "opacity-80", className)}
        >
            <Link to={to.path} {...spread}>
                {isPending ? (
                    <Loader className="mr-2" />
                ) : Icon ? (
                    <Icon className="mr-2 h-4 w-4" />
                ) : undefined}
                <span>{children}</span>
            </Link>
        </Component>
    );
};
